// @ts-ignore
import type { UiSchema } from '@genesislcap/foundation-forms';
import { formatDate, formatDateTime, formatNumeric, formatTime, submitBondBid } from './bond-bid-utils';
import { normalStarIcon } from './favourite-checkbox';
import { ColDef } from '@ag-grid-community/core';
import {
  EditDeleteCellRenderer,
  FocusableCellRenderer,
  SubmitButtonCellRenderer,
  BidPriceCellEditor,
  AuditButtonCellRenderer,
} from '../components/cell-renderers';
import { BondStatusBadgeCellRenderer } from '../components/bond-status-badge-cell';
import { BidStatusBadgeCellRenderer } from '../components/bid-status-badge-cell';
import { User } from '@genesislcap/foundation-user';
import {
  normalizeBidStatus,
  isSubmittableBidStatus,
  hasRowPendingChanges,
} from './bulk-response-upload/submit-eligibility';

// Helper function to add menuTabs to all columns
function addMenuTabsToColumns(columns: Array<ColDef<any>>): Array<ColDef<any>> {
  return columns.map(col => ({
    ...col,
    menuTabs: ['generalMenuTab']
  }));
}

function getCommonColumns(): Array<ColDef> {
  const columns = [
    { field: 'BOND_ID', headerName: 'Reference#' },
    {
      field: 'RECEIVED_TIME',
      headerName: 'Received Time',
      valueFormatter: (params) => (params.value ? formatTime(params.value) : ''),
    },
    {
      field: 'DUE_TIME',
      headerName: 'Due Time',
      valueFormatter: (params) => (params.value ? formatTime(params.value) : ''),
    },
    {
      field: 'FIRM_TIME',
      headerName: 'Firm Until',
      valueFormatter: (params) => (params.value ? formatTime(params.value) : ''),
    },
    { field: 'ISSUER_NAME', headerName: 'Issuer' },
    { field: 'STATE', headerName: 'State' },
    {
      field: 'STATUS',
      headerName: 'RFQ Status',
      cellRenderer: BondStatusBadgeCellRenderer,
      width: 200,
      minWidth: 200,
      pinned: 'left' as const,
      sort: 'asc' as const,
    },
    { field: 'CUSIP', headerName: 'CUSIP' },
    {
      field: 'COUPON_RATE',
      headerName: 'Coupon',
      valueFormatter: (params) => formatNumeric(params.data.COUPON_RATE, 3, true),
    },
    {
      field: 'MATURITY_DATE',
      headerName: 'Maturity',
      valueFormatter: (params) => (params.value ? formatDate(params.value) : ''),
    },
    { field: 'PRE_REFUND_DATE', headerName: 'Pre-Refund Date', valueFormatter: (params) => (params.value ? formatDate(params.value) : '') },
    { field: 'CALL_DATE', headerName: 'Call Date', valueFormatter: (params) => (params.value ? formatDate(params.value) : '') },
    {
      field: 'CALL_PRICE',
      headerName: 'Call Price',
      valueFormatter: (params) => formatNumeric(params.data.CALL_PRICE),
    },
    { field: 'WORKOUT_DATE', headerName: 'Workout Date', valueFormatter: (params) => (params.value ? formatDate(params.value) : '') },
    { field: 'SECTOR', headerName: 'Sector' },
    { field: 'ISSUER_RATINGS', headerName: 'Ratings (S&P/Fitch/Moodys)' },
    { field: 'TAX_STATUS', headerName: 'Fed Taxable' },
    { field: 'AMT', headerName: 'AMT' },
    { field: 'SETTLEMENT_DATE', headerName: 'Settlement Date', valueFormatter: (params) => (params.value ? formatDate(params.value) : ''), },
    { field: 'INSURER', headerName: 'Insurer' },
    { field: 'SOURCE', headerName: 'Source' },
    {
      field: 'OUTSTANDING_VOLUME',
      headerName: 'Outstanding Volume',
      valueFormatter: (params) => formatNumeric(params.data.OUTSTANDING_VOLUME),
      hide: true,
    },
    {
      field: 'BOND_PRICE_TYPE',
      headerName: 'Bond Price Type',
      hide: true,
    },
    { field: 'BID_ID', headerName: 'Bid ID', hide: true, suppressColumnsToolPanel: true },
  ];
  
  return addMenuTabsToColumns(columns);
}

export const PLATFORM_ACTIVITY_GRID_COLUMNS = addMenuTabsToColumns([
  ...getCommonColumns(),
  ...getBidColumns(true),
  { field: 'ORDER_ORIGINATION_FIRM', headerName: 'Order Origination Firm' },
]);

export const TODAYS_ACTIVITY_GRID_COLUMNS = addMenuTabsToColumns([
  ...getCommonColumns(),
  {
    field: 'TRADER',
    headerName: 'Trader',
    pinned: 'right' as const,
    width: 120,
    minWidth: 120,
  },
  {
    field: 'BID_STATUS',
    headerName: 'Bid Status',
    cellRenderer: BidStatusBadgeCellRenderer,
    pinned: 'right' as const,
    width: 140,
    minWidth: 140,
  },
  {
    field: 'IOI_SIZE',
    headerName: 'Quantity',
    pinned: 'right' as const,
    width: 110,
    minWidth: 110,
    valueFormatter: (params) => formatNumeric(params.data.IOI_SIZE),
    valueGetter: (params) => params.data.IOI_SIZE,
  },
  {
    field: 'BID_PRICE',
    headerName: 'Response',
    pinned: 'right' as const,
    width: 130,
    minWidth: 130,
  },
  {
    field: 'BID_PRICE_TYPE',
    headerName: 'Response Type',
    pinned: 'right' as const,
    valueGetter: (params) => {
      const bondType = params.data.BOND_PRICE_TYPE;
      if (bondType && bondType !== 'None') {
        return bondType;
      }
      return params.data.BID_PRICE_TYPE;
    },
    width: 150,
    minWidth: 150,
  },
  {
    field: 'FEEDBACK',
    headerName: 'Feedback',
    pinned: 'right' as const,
    width: 250,
    minWidth: 250,
  },
]);

export interface LiveActivityColumnsOptions {
  onBidSubmitted?: (bondId: string, bidPrice: string, bidPriceType: string) => void;
}

export function getLiveActivityColumns(user?: User, options?: LiveActivityColumnsOptions): Array<ColDef> {
  
  const columns = [
    {
      field: 'WATCHING',
      headerName: '',
      width: 60,
      suppressMenu: true,
      suppressColumnsToolPanel: true,
      headerComponentParams: {
        template: `<i style='padding-top:7px;padding-left:1px'>${normalStarIcon()}</i>`,
      },
      cellRenderer: (params) => {
        const eDiv = document.createElement('div');
        eDiv.innerHTML = `<favourite-checkbox watching=${params.data.WATCHING} cusip="${params.data.CUSIP}" style='padding-left:2px'></favourite-checkbox>`;
        return eDiv;
      },
      pinned: true,
      lockPinned: true,
    },
    ...getCommonColumns(),
    ...getBidColumns(user?.hasPermission('ViewPlatformActivity'), options),
  ];
  
  return addMenuTabsToColumns(columns);
}

function getBidColumns(platformActivityView: boolean, options?: LiveActivityColumnsOptions): Array<ColDef> {

  // Hide bid columns if user has ViewPlatformActivity right
  if (platformActivityView) {
    return [
      {
        field: 'IOI_SIZE',
        headerName: 'Quantity',
        valueFormatter: (params) => formatNumeric(params.data.IOI_SIZE),
        valueGetter: (params) => params.data.IOI_SIZE,
        width: 110,
        minWidth: 110,
      },
    ];
  }
  
  return [
    {
      field: 'BID_STATUS',
      headerName: 'Bid Status',
      cellRenderer: BidStatusBadgeCellRenderer,
      tooltipValueGetter: (params) => {
        const status = params.data?.BID_STATUS;
        if (status === 'Rejected') {
          return params.data?.FEEDBACK || '';
        }
        return undefined; // no tooltip
      },
      pinned: 'right' as const,
      width: 160,
      minWidth: 160,
    },
    {
      field: 'IOI_SIZE',
      headerName: 'Quantity',
      valueFormatter: (params) => formatNumeric(params.data.IOI_SIZE),
      valueGetter: (params) => params.data.IOI_SIZE,
      width: 110,
      minWidth: 110,
      pinned: 'right' as const,
    },
    {
      field: 'BID_PRICE_TYPE',
      headerName: 'Response Type',
      pinned: 'right' as const,
      cellClass: 'bid-price-cell',
      valueGetter: (params) => {
        const bondType = params.data.BOND_PRICE_TYPE;
        if (bondType && bondType !== 'None') {
          params.data.BID_PRICE_TYPE = bondType;
          return bondType;
        }
        // Default to 'Price' when not set
        if (!params.data.BID_PRICE_TYPE || params.data.BID_PRICE_TYPE === 'None') {
          params.data.BID_PRICE_TYPE = 'Price';
        }
        return params.data.BID_PRICE_TYPE;
      },
      editable: (params) => {
        if (params.data.isEditing) {
          return true;
        }
        const bondType = params.data.BOND_PRICE_TYPE;
        const isCancelled = params.data.BID_STATUS?.toUpperCase() === 'CANCELLED';
        const isPending = params.data.BID_STATUS?.toUpperCase().startsWith('PENDING');

        // If BOND_PRICE_TYPE is set to anything other than 'None', don't allow editing
        if (bondType && bondType !== 'None') {
          return false;
        }

        // Allow editing if cancelled (but still respect BOND_PRICE_TYPE constraint)
        if (isCancelled) {
          return !bondType || bondType === 'None';
        }

        // Allow editing if status is DUE_IN and not pending
        if (params.data.STATUS === 'DUE_IN' && !isPending) {
          return true;
        }

        return false;
      },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: ['Price', 'Spread', 'Yield'] },
      singleClickEdit: true,
      width: 150,
      minWidth: 150,
      cellClassRules: {
        'ag-disabled': (params) => !!params.data.BID_ID,
      },
      cellStyle: (params) => {
        const isEditable =
          typeof params.colDef.editable === 'function'
            ? params.colDef.editable(params)
            : !!params.colDef.editable;

        const baseStyle = isEditable
          ? { cursor: 'text', opacity: 1 }
          : { cursor: 'not-allowed', opacity: 1 };

        if (params.data.BID_STATUS?.toUpperCase() === 'CANCELLED') {
          return { ...baseStyle, color: '#888888' };
        }

        return baseStyle;
      },
      cellRenderer: FocusableCellRenderer,
      onCellValueChanged: (params) => {
        // When price type changes, move focus to BID_PRICE and start editing
        const rowIndex = params.node.rowIndex;
        setTimeout(() => {
          params.api.setFocusedCell(rowIndex, 'BID_PRICE');
          params.api.startEditingCell({ rowIndex, colKey: 'BID_PRICE' });
        }, 0);
      },
    },
    {
      field: 'BID_PRICE',
      headerName: 'Response',
      pinned: 'right' as const,
      cellClass: 'bid-price-cell',
      editable: (params) => {
        const isCancelled = params.data.BID_STATUS?.toUpperCase() === 'CANCELLED';
        const isPending = params.data.BID_STATUS?.toUpperCase().startsWith('PENDING');
        
        // Allow editing if cancelled or original conditions, but not if pending
        return (
          params.data.isEditing ||
          isCancelled ||
          (params.data.STATUS === 'DUE_IN' && !isPending)
        );
      },
      cellEditor: BidPriceCellEditor,
      singleClickEdit: true,
      width: 130,
      minWidth: 130,
      cellClassRules: {
        'ag-disabled': (params) => !!params.data.BID_ID,
      },
      cellRenderer: FocusableCellRenderer,
      cellStyle: (params) => {
        if (params.data.isEditing) {
          return {
            cursor: 'text',
            opacity: 1,
          };
        }
        const isEditable =
          typeof params.colDef.editable === 'function'
            ? params.colDef.editable(params)
            : !!params.colDef.editable;

        const baseStyle = isEditable
          ? {
              cursor: 'text',
              opacity: 1,

            }
          : {
              cursor: 'not-allowed',
              opacity: 1,

            };

        // Check if bid status is cancelled
        if (params.data.BID_STATUS?.toUpperCase() === 'CANCELLED') {
          return { ...baseStyle, color: '#888888' };
        }

        return baseStyle;
      },
    },
    {
      field: 'CALCULATED_BID_PRICE',
      headerName: 'Price',
      width: 120,
      minWidth: 120,
      pinned: 'right' as const,
      hide: true,
    },
    {
      field: 'CALCULATED_YIELD',
      headerName: 'Yield',
      width: 120,
      minWidth: 120,
      pinned: 'right' as const,
      hide: true,
    },
    {
      field: 'SUBMIT_BID',
      headerName: '',
      pinned: 'right' as const,
      suppressMenu: true,
      suppressColumnsToolPanel: true,
      cellRendererSelector: (params) => {
        const { data } = params;
        const hasChanges = hasRowPendingChanges(data);

        const status = (data.BID_STATUS as string) ?? '';
        const statusNorm = normalizeBidStatus(status);
        const hasExistingBid = data.BID_ID != null && data.BID_ID !== '' && statusNorm;
        const isSubmittableStatus = !hasExistingBid || isSubmittableBidStatus(status);

        // Show submit when submittable and (changes, no DUE_IN bid, or cancelled)
        if (
          isSubmittableStatus &&
          (hasChanges ||
            !(data.BID_ID && data.STATUS === 'DUE_IN') ||
            (data.BID_STATUS && normalizeBidStatus(data.BID_STATUS as string) === 'CANCELLED'))
        ) {
          return { component: SubmitButtonCellRenderer };
        }
        // Show submit for non-submittable statuses (e.g. REJECTED) only when there are pending changes,
        // so user can click and see backend error; after successful submit (hasChanges cleared) show Edit/Delete
        if (hasExistingBid && !isSubmittableStatus && hasChanges) {
          return { component: SubmitButtonCellRenderer };
        }

        return { component: EditDeleteCellRenderer };
      },
      cellRendererParams: {
        actionName: 'Submit',
        actionClick: async (rowData) => {
          const { BOND_ID, BID_PRICE, BID_PRICE_TYPE } = rowData;
          const result = await submitBondBid(BOND_ID, BID_PRICE, BID_PRICE_TYPE);
          if (result.success && options?.onBidSubmitted) {
            options.onBidSubmitted(BOND_ID, BID_PRICE, BID_PRICE_TYPE);
          }
          return result;
        },
        isDisabled: (rowData) => {
          if (rowData.STATUS !== 'DUE_IN') return true;
          if (!rowData.BID_PRICE) return true;
          if (!rowData.DUE_TIME) return true;
          const now = Date.now();
          const dueTime = new Date(rowData.DUE_TIME).getTime();
          if (now > dueTime) return true;
          // Do not disable by BID_STATUS: let user click Submit and show backend error (e.g. REJECTED)
          return false;
        },
        appearance: 'primary',
        actionButtonStyle: 'height: 25px; min-width: 0; padding: 0;',
      },
      width: 110,
      minWidth: 110,
    },
    {
      field: 'AUDIT',
      headerName: 'Audit',
      pinned: 'right' as const,
      cellRenderer: AuditButtonCellRenderer,
      suppressMenu: true,
      suppressColumnsToolPanel: true,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      sortable: false,
      filter: false,
    },

  ];
}

export const bidFormUiSchema: UiSchema = {
  type: 'VerticalLayout',
  elements: [
    {
      type: 'Control',
      scope: '#/properties/BID_AMOUNT',
      label: 'Bid Amount',
    },
    {
      type: 'Control',
      scope: '#/properties/BID_QUANTITY',
      label: 'Bid Quantity',
    },
  ],
};

export function getBidAuditColumns(): Array<ColDef> {
  return [
    {
      field: 'CREATED_AT',
      headerName: 'Created At',
      valueFormatter: (params) => (params.value ? formatDateTime(params.value) : ''),
      width: 120,
      minWidth: 120,
    },
    {
      field: 'TRADER',
      headerName: 'Trader',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'AUDIT_EVENT_DATETIME',
      headerName: 'Last Modified At',
      valueFormatter: (params) => (params.value ? formatDateTime(params.value) : ''),
      width: 170,
      minWidth: 170,
    },
    {
      field: 'AUDIT_EVENT_USER',
      headerName: 'Last Modified By',
      width: 170,
      minWidth: 170,
    },
    {
      field: 'BID_STATUS',
      headerName: 'Bid Status',
      width: 160,
      minWidth: 160,
    },
    {
      field: 'BID_PRICE',
      headerName: 'Response',
      width: 130,
      minWidth: 130,
    },
    {
      field: 'BID_PRICE_TYPE',
      headerName: 'Response Type',
      width: 150,
      minWidth: 150,
    },
    {
      field: 'CALCULATED_BID_PRICE',
      headerName: 'Price',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'CALCULATED_YIELD',
      headerName: 'Yield',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'AUDIT_EVENT_TYPE',
      headerName: 'Event Type',
      width: 150,
      minWidth: 150,
    },
    {
      field: 'BOND_ID',
      headerName: 'Bond ID',
      hide: true,
    },
  ];
}

export function getBidAuditHistoryColumns(): Array<ColDef> {
  return addMenuTabsToColumns([
    {
      field: 'BID_STATUS',
      headerName: 'Bid Status',
      width: 140,
      minWidth: 140,
    },
    {
      field: 'BID_PRICE',
      headerName: 'Response',
      width: 130,
      minWidth: 130,
    },
    {
      field: 'BID_PRICE_TYPE',
      headerName: 'Response Type',
      width: 150,
      minWidth: 150,
    },
    {
      field: 'CALCULATED_BID_PRICE',
      headerName: 'Price',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'CALCULATED_YIELD',
      headerName: 'Yield',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'AUDIT_EVENT_TYPE',
      headerName: 'Event Type',
      width: 100,
      minWidth: 100,
    },
    {
      field: 'TRADER',
      headerName: 'Trader',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'CREATED_AT',
      headerName: 'Created At',
      width: 150,
      minWidth: 150,
      valueFormatter: (params) => (params.value ? formatTime(params.value) : ''),
    },
    {
      field: 'AUDIT_EVENT_USER',
      headerName: 'Modified By',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'AUDIT_EVENT_DATETIME',
      headerName: 'Modified At',
      width: 150,
      minWidth: 150,
      valueFormatter: (params) => (params.value ? formatTime(params.value) : ''),
    },
  ]);
}

export function getBidsGridColumns(): Array<ColDef> {
  const columns = [
    {
      field: 'BOND_ID',
      headerName: 'Reference#',
      width: 300,
      minWidth: 300,
      pinned: 'left' as const,
      hide: true
    },
    {
      field: 'TRADER',
      headerName: 'Trader',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'BID_STATUS',
      headerName: 'Bid Status',
      cellRenderer: BidStatusBadgeCellRenderer,
      width: 140,
      minWidth: 140,
    },
    { field: 'CUSIP', headerName: 'CUSIP' },
    {
      field: 'BID_PRICE',
      headerName: 'Response',
      width: 130,
      minWidth: 130,
    },
    {
      field: 'BID_PRICE_TYPE',
      headerName: 'Response Type',
      width: 150,
      minWidth: 150,
    },
    {
      field: 'COUNTERPARTY_ID',
      headerName: 'Counterparty',
      width: 220,
      minWidth: 220,
    },
    {
      field: 'CALCULATED_BID_PRICE',
      headerName: 'Price',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'CALCULATED_YIELD',
      headerName: 'Yield',
      width: 120,
      minWidth: 120,
    },
    {
      field: 'FEEDBACK',
      headerName: 'Feedback',
      width: 250,
      minWidth: 250,
    },
  ];
  
  return addMenuTabsToColumns(columns);
}
